/**
 * Spreadsheet helpers for bulk product import.
 *
 * SheetJS (xlsx) is loaded on demand from a CDN rather than bundled, because the
 * build environment has no access to the npm registry. If the CDN can't be
 * reached, .csv files still work via a small built-in parser, and .xlsx files
 * surface a clear "use CSV instead" error.
 */

const XLSX_CDN =
  'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type XLSXNS = any;

let xlsxPromise: Promise<XLSXNS> | null = null;

function loadXLSX(): Promise<XLSXNS> {
  const w = window as unknown as { XLSX?: XLSXNS };
  if (w.XLSX) return Promise.resolve(w.XLSX);
  if (xlsxPromise) return xlsxPromise;
  xlsxPromise = new Promise<XLSXNS>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = XLSX_CDN;
    s.async = true;
    s.onload = () =>
      w.XLSX
        ? resolve(w.XLSX)
        : reject(new Error('Excel library loaded but was unavailable.'));
    s.onerror = () => {
      xlsxPromise = null; // allow a retry on the next attempt
      reject(
        new Error(
          'Could not load the Excel reader (offline or blocked). ' +
            'Save your file as .csv and upload that instead.',
        ),
      );
    };
    document.head.appendChild(s);
  });
  return xlsxPromise;
}

export type SheetRow = Record<string, string>;

/** Minimal RFC-4180-ish CSV parser (handles quotes, commas, newlines). */
function parseCsv(text: string): SheetRow[] {
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  // Strip a UTF-8 BOM if present.
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(field);
      field = '';
      if (row.some((v) => v.trim() !== '')) rows.push(row);
      row = [];
    } else {
      field += c;
    }
  }
  if (field !== '' || row.length) {
    row.push(field);
    if (row.some((v) => v.trim() !== '')) rows.push(row);
  }
  if (rows.length === 0) return [];
  const headers = rows[0].map((h) => h.trim());
  return rows.slice(1).map((r) => {
    const obj: SheetRow = {};
    headers.forEach((h, i) => {
      obj[h] = (r[i] ?? '').trim();
    });
    return obj;
  });
}

/** Parse an uploaded .xlsx/.xls/.csv into header-keyed row objects. */
export async function parseSpreadsheet(file: File): Promise<SheetRow[]> {
  const isCsv = /\.csv$/i.test(file.name);
  try {
    const XLSX = await loadXLSX();
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const json = XLSX.utils.sheet_to_json(ws, { defval: '' }) as SheetRow[];
    // Trim keys + string values so header/value whitespace never breaks matching.
    return json.map((r) => {
      const obj: SheetRow = {};
      Object.keys(r).forEach((k) => {
        obj[k.trim()] = String(r[k] ?? '').trim();
      });
      return obj;
    });
  } catch (err) {
    if (isCsv) return parseCsv(await file.text());
    throw err;
  }
}

/** Column headers for the import template (must match the parser mapping). */
export const TEMPLATE_HEADERS = [
  'Name',
  'Category',
  'Price',
  'SKU',
  'Description',
  'GST Rate %',
  'GST Applicable',
  'Available',
] as const;

function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Download a starter template. Tries a real .xlsx (via SheetJS); if the library
 * can't load, falls back to a .csv (which Excel opens natively).
 * `exampleCategory` seeds the example row with a real category name if available.
 */
export async function downloadTemplate(exampleCategory?: string): Promise<void> {
  const example = [
    'Classic Burger',
    exampleCategory || 'Burgers',
    '199',
    'BRG-001',
    'Juicy beef patty',
    '5',
    'yes',
    'yes',
  ];
  try {
    const XLSX = await loadXLSX();
    const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS as unknown as string[], example]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Products');
    const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    triggerDownload(
      new Blob([out], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      'product-import-template.xlsx',
    );
  } catch {
    const csv =
      [TEMPLATE_HEADERS.join(','), example.map(csvCell).join(',')].join('\r\n') + '\r\n';
    triggerDownload(
      new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }),
      'product-import-template.csv',
    );
  }
}
